import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signIn,
  SignInOutput,
  signOut,
} from '@aws-amplify/auth';
import { datadogRum } from '@datadog/browser-rum';
import { mapValues } from 'lodash-es';
import { firstValueFrom, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { sha256 as Sha256 } from 'sha.js';

import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { AppInitService } from '@shared/services/app-init.service';
import { GqlService, PermissionType } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import type { AllowedRolesPermissions } from '@shared/types/auth.type';

import { AuthQuery } from './auth.query';
import { AuthState, AuthStore } from './auth.store';
import { LoggedInUser } from './LoggedInUser';
import { environment } from '../../../../environments/environment';

export const broadCastChannel = new BroadcastChannel('authentication');

@Injectable({ providedIn: 'root' })
export class AuthService {
  private appInitService = inject(AppInitService);
  private authQuery = inject(AuthQuery);
  private authStore = inject(AuthStore);
  private gqlService = inject(GqlService);
  private ld = inject(LaunchDarklyService);
  private router = inject(Router);

  isSysAdmin = toSignal(this.authQuery.select('is_sys_admin'), { requireSync: true });
  permissions = toSignal(this.authQuery.select('permissions'), { requireSync: true });
  state = toSignal(this.authQuery.select(), { requireSync: true });

  latestUserParams: undefined | { email: string; password: string };

  private tokenLookup = new Map<string, LoggedInUser>();
  private tokenPromiseLookup = new Map<string, Promise<LoggedInUser>>();

  /**
   * @deprecated use either `getPermission` or `getPermissions` instead
   */
  $isAuthorized(allowedRolesPermissions: AllowedRolesPermissions) {
    return toSignal(this.isAuthorized$(allowedRolesPermissions), { requireSync: true });
  }

  async confirmSignUp({ code, username }: { code: string; username: string }) {
    let result = true;

    try {
      await confirmSignUp({
        confirmationCode: code,
        username,
      });
    } catch {
      result = false;
    }

    return result;
  }

  constructor() {
    broadCastChannel.onmessage = (event) => {
      if (event.data === 'Logout') {
        void this.router.navigate([`/${ROUTING_PATH.LOGIN}`], { queryParams: {} });
        window.location.reload();
      }
    };
  }

  async getLoggedInUser(showUserLogInError: boolean = true) {
    try {
      const session = await this.getUserSession();
      const jwtToken = session?.tokens?.idToken?.toString();

      if (!jwtToken) return null;

      const k = new Sha256().update(jwtToken).digest('hex');
      let loggedInUser = this.tokenLookup.get(k);

      if (!loggedInUser) {
        try {
          loggedInUser = await this.getTokenPromise(k);
        } finally {
          // eslint-disable-next-line max-depth
          if (loggedInUser) {
            this.tokenLookup.set(k, loggedInUser);
          }

          this.tokenPromiseLookup.delete(k);
        }

        return loggedInUser;
      }
    } catch (err) {
      if (showUserLogInError) console.error('Failed to get the logged in user', err);
    }

    return null;
  }

  getPermission(permission: PermissionType) {
    return computed(() => {
      const isSysAdmin = this.isSysAdmin();
      const permissions = this.permissions();

      return isSysAdmin || permissions.includes(permission);
    });
  }

  getPermissions<T extends Record<string, `${PermissionType}`>>(perms: T) {
    return computed<Record<keyof T, boolean>>(() => {
      const isSysAdmin = this.isSysAdmin();
      const permissions = this.permissions();

      return mapValues(perms, (perm) => isSysAdmin || permissions.includes(perm as PermissionType));
    });
  }

  private getTokenPromise(k: string): Promise<LoggedInUser> {
    // this internal/helper method is for preventing concurrent requests to the backend
    // while the information about the same token is still being retrieved
    const promiseInProgress = this.tokenPromiseLookup.get(k);

    if (promiseInProgress) {
      return promiseInProgress;
    }

    // eslint-disable-next-line no-async-promise-executor, @typescript-eslint/no-misused-promises
    const result = new Promise<LoggedInUser>(async (resolve, reject) => {
      try {
        const response = await firstValueFrom(this.gqlService.loggedInUser$());

        if (response.success) {
          resolve(new LoggedInUser(response.data));
        } else {
          reject(new Error(JSON.stringify(response.errors)));
        }
      } catch (e) {
        reject(e);
      }
    });

    this.tokenPromiseLookup.set(k, result);

    return result;
  }

  getUserSession() {
    try {
      return fetchAuthSession();
    } catch (err) {
      console.error(err);

      return null;
    }
  }

  initDatadoghq(attributes: AuthState, role: string) {
    datadogRum.init({
      applicationId: '62556293-3de9-4d37-a1e9-83d249e41bc7',
      clientToken: 'pub2625c0074cf36ade11978af0b16c4588',
      defaultPrivacyLevel: 'allow',
      env: environment.stage,
      service: 'auxilius',
      sessionReplaySampleRate: 100,
      sessionSampleRate: 100,
      site: 'datadoghq.com',
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      version: this.appInitService.APP_VERSION,
    });

    datadogRum.setUser({
      email: attributes.email,
      id: attributes.sub,
      name: `${attributes.given_name} ${attributes.family_name}`,
      role,
    });
  }

  async isAuthenticated() {
    let isAuthenticated = true;

    try {
      await getCurrentUser();
    } catch {
      isAuthenticated = false;
    }

    return isAuthenticated;
  }

  /**
   * @deprecated use either `getPermission` or `getPermissions` instead
   */
  isAuthorized$(allowedRolesPermissions: AllowedRolesPermissions): Observable<boolean> {
    return this.authQuery.select().pipe(
      map((user) => {
        return this.isAuthorizedSync(new LoggedInUser(user), allowedRolesPermissions);
      })
    );
  }

  isAuthorizedSync(user: LoggedInUser, allowedRolesPermissions: AllowedRolesPermissions) {
    if (user.IsSysAdmin()) {
      return true;
    }

    if (allowedRolesPermissions?.sysAdminsOnly) {
      return false;
    }

    const allowedRoles: Array<string> = [];

    if (Array.isArray(allowedRolesPermissions?.roles)) {
      allowedRolesPermissions.roles.forEach((r) => {
        if (r) {
          allowedRoles.push(r.toUpperCase());
        }
      });
    }

    if (user.hasRole(...allowedRoles)) {
      return true;
    }

    const allowedPermissions: Array<string> = [];

    if (Array.isArray(allowedRolesPermissions?.permissions)) {
      allowedRolesPermissions.permissions.forEach((p) => {
        if (p) {
          allowedPermissions.push(p.toUpperCase());
        }
      });
    }

    return user.hasPermission(...allowedPermissions);
  }

  async setUserAttributes(options: { forceRefresh?: boolean; showUserLogInError?: boolean } = {}) {
    const userAttributes = await fetchUserAttributes();
    const session = await fetchAuthSession({ forceRefresh: options.forceRefresh });
    const payload = session.tokens?.idToken?.payload;

    const attributes: AuthState = {
      department: [],
      email: userAttributes.email || '',
      family_name: userAttributes.family_name || '',
      given_name: userAttributes.given_name || '',
      is_admin: (<string[]>payload?.['cognito:groups'] || []).includes('Admin'),
      is_sys_admin: false,
      organization_id: null,
      permissions: [],
      roles: [],
      sub: userAttributes.sub || '',
      title: '',
      trial_id: <string>payload?.trial_id,
    };

    this.authStore.update(attributes);

    await this.ld.changeUser({
      custom: {
        clientName: environment.launchDarkly.clientName,
      },
      email: attributes.email,
      firstName: attributes.given_name,
      key: attributes.sub,
      lastName: attributes.family_name,
    });

    try {
      const loggedInUser = await this.getLoggedInUser(options.showUserLogInError);

      if (loggedInUser) {
        let role = loggedInUser.getRoles().indexOf('Admin') > -1 ? 'Admin' : 'Read Only';

        role = attributes.email.indexOf('@auxili.us') > -1 ? 'Auxilius Admin' : role;

        this.authStore.update({
          ...attributes,
          department: loggedInUser.getDepartment(),
          is_sys_admin: loggedInUser.IsSysAdmin(),
          organization_id: loggedInUser.getOrganizationId(),
          permissions: loggedInUser.getPermissions() as PermissionType[],
          roles: loggedInUser.getRoles(),
          title: loggedInUser.getTitle(),
        });

        window?.pendo?.initialize({
          account: {
            id: environment.analytics.Pendo.accountId, // 'ACCOUNT-UNIQUE-ID', // Required if using Pendo Feedback
            // name:         // Optional
            // is_paying:    // Recommended if using Pendo Feedback
            // monthly_value:// Recommended if using Pendo Feedback
            // planLevel:    // Optional
            // planPrice:    // Optional
            // creationDate: // Optional
            // You can add any additional account level key-values here,
            // as long as it's not one of the above reserved names.
          },
          visitor: {
            departments: loggedInUser.getAllDepartments(),
            email: attributes.email, // Recommended if using Pendo Feedback, or NPS Email
            full_name: `${attributes.given_name} ${attributes.family_name}`,
            id: attributes.sub, // 'VISITOR-UNIQUE-ID', // Required if user is logged in
            role, // Optional
            // You can add any additional visitor level key-values here,
            // as long as it's not one of the above reserved names.
          },
        });

        this.initDatadoghq(attributes, role);
      }
    } catch (e) {
      console.error(e);
    }

    return attributes;
  }

  signIn(email: string, password: string): Promise<SignInOutput> {
    return signIn({ password, username: email });
  }

  async signOut() {
    localStorage.removeItem(LocalStorageKey.STAGE_BANNER);
    localStorage.removeItem(LocalStorageKey.TIMELINE_BANNER);
    localStorage.removeItem(LocalStorageKey.LOGIN_LAST_URL);
    localStorage.removeItem(LocalStorageKey.LAST_ACTIVE_DATE);

    try {
      await signOut();
    } catch (e) {
      // UserAlreadyAuthenticatedException
      console.error(e);
    }

    this.authStore.reset();
    this.tokenLookup.clear();
    this.tokenPromiseLookup.clear();
    broadCastChannel.postMessage('Logout');
  }

  updatePermissions$() {
    return this.gqlService.userPermissions$().pipe(
      tap(({ data, success }) => {
        if (data && success) {
          this.authStore.update({
            permissions: data.permissions,
          });
        }
      })
    );
  }
}

export const injectPermissions = <T extends Record<string, `${PermissionType}`>>(perms: T) => {
  const authService = inject(AuthService);

  return authService.getPermissions(perms);
};
